/**
 * feelBudget (S195 / BL-278 / BL-279) — THE feedback-hierarchy table.
 *
 * The single tuning home for the Fire → Impact → Death loop's loudness
 * ladder (the `palette.ts` precedent: one Pflegetabelle, no scattered magic
 * numbers). Canon: docs/design/COMBAT_FEEL_DIRECTION.md — tiers exist so
 * LOWER tiers stay quiet; a beat's loudness is decided by its TIER, never by
 * which handler happened to run last.
 *
 *   hit < kill < crit-kill < combo-milestone < wave-clear < boss-kill
 *       ≤ payload-gold < boss-ARRIVAL < near-loss < game-end
 *
 * Pure data + pure derivation helpers — DOM-free (core law), sim-free (the
 * sim never reads it; render/UI consumers only, so the golden masters cannot
 * move). Colours reference the central palette tokens, never raw hex.
 *
 * Amplitude law: shakes stay ≤ ~4 CSS px (the design cap) with ONE ratified
 * exception — the payload board-nuke theatre (T5, S195-F07 ruled sanctioned).
 * Flash alphas are requests INTO the FlashGovernor (stack-cap 0.6, ≤3/s) —
 * the governor stays the photosensitivity gate for every entry here.
 */
import { COLORS } from "./constants.ts";

/** GameLoop freeze tiers (mirrors engine/GameLoop's HitStopTier). */
export type FeelHitStop = "micro" | "short" | "payload";

/** One screen-space beat budget. Only the channels a tier OWNS are present. */
export interface FeelBeat {
  /** [amplitude CSS-px, duration ms] */
  shake?: readonly [number, number];
  /** [peak zoom, duration ms] — the hero transform where present (law #4). */
  punch?: readonly [number, number];
  /** [colour token, requested alpha] — routed through the FlashGovernor. */
  flash?: readonly [string, number];
  /** Rare-canon-beat flag: bypasses the governor's ≤3/s rate gate (never the
   *  stack cap). ONLY boss-kill / boss-arrival class single beats. */
  flashPriority?: boolean;
  /** [colour token, duration ms] */
  vignette?: readonly [string, number];
  /** [peak chroma offset, duration ms] */
  chroma?: readonly [number, number];
  hitStop?: FeelHitStop;
}

/** Banner slot priorities (GameBanner arbitration — anti-fight law #6).
 *  A lower-priority show never replaces an active higher one. */
export const BANNER_PRIORITY = {
  info: 0,
  comboMilestone: 3,
  waveClear: 4,
  bossArrival: 5,
  gameEnd: 7,
} as const;

/** The kill tier's combo-scaled shake (BL-208 lineage, now canon). */
export const KILL_FEEL = {
  shakeBase: 1.4,
  shakePerMult: 0.3,
  shakeMax: 3.0,
  shakeMs: 110,
  /** Anti-fight law #5 — the +N score pop spawns this far ABOVE the −N
   *  damage number (both anchored at y−10 world-px before S195; 22 px clears
   *  the −N's 1.4× pop-in glyph height so they never kiss). */
  scorePopLift: 22,
  /** Same-frame multi-kill +Ns fan upward by this per kill (law #5). */
  scorePopFanStep: 8,
  scorePopFanMax: 4,
  /** Soft score-tick throttle (unchanged S189 value). */
  tickThrottleMs: 70,
} as const;

/** Ordinary-kill shake amplitude for a live score multiplier. */
export function killShakeAmp(mult: number): number {
  return Math.min(
    KILL_FEEL.shakeBase + (mult - 1) * KILL_FEEL.shakePerMult,
    KILL_FEEL.shakeMax,
  );
}

/** Combo-milestone flash alpha ladder (×5 / ×7 / ×10). */
export function milestoneFlashAlpha(mult: number): number {
  return mult >= 10 ? 0.18 : mult >= 7 ? 0.13 : 0.1;
}

/** Core-hit (enemy.reachedEnd) budgets — damage-scaled, capped. */
export const CORE_HIT_FEEL = {
  shakeBase: 2,
  shakePerDamage: 1,
  shakeMax: 6,
  shakeMs: 300,
  flashBase: 0.4,
  flashPerDamage: 0.1,
  flashMax: 0.8,
  vignetteMs: 1000,
} as const;

export function coreHitShakeAmp(damage: number): number {
  return Math.min(
    CORE_HIT_FEEL.shakeBase + damage * CORE_HIT_FEEL.shakePerDamage,
    CORE_HIT_FEEL.shakeMax,
  );
}

export function coreHitFlashAlpha(damage: number): number {
  return Math.min(
    CORE_HIT_FEEL.flashBase + damage * CORE_HIT_FEEL.flashPerDamage,
    CORE_HIT_FEEL.flashMax,
  );
}

/**
 * The hero/context beat table. Values are today's shipped constants unless
 * the S195 bible tuned them — tuned entries say so.
 */
export const FEEL_BEATS = {
  /** T6 boss ARRIVAL — dread, not a punch (BL-237 values, now canon). */
  bossArrival: {
    shake: [3, 350],
    flash: [COLORS.MALWARE_RED, 0.18],
    flashPriority: true,
    vignette: [COLORS.MALWARE_RED, 700],
    chroma: [0.005, 320],
    hitStop: "micro",
  },
  /** T4 boss KILL — punch-led per law #4 (shake 3.5→2.6, ≤75 % support, so
   *  the camera punch is the hero and the shake no longer equals arrival). */
  bossKill: {
    punch: [1.12, 160],
    chroma: [0.008, 220],
    hitStop: "short",
    shake: [2.6, 320],
    flash: [COLORS.SENTINEL_GREEN, 0.22],
    flashPriority: true,
  },
  /** Tower reaches GOLD (BL-237 values). */
  goldReached: {
    punch: [1.08, 140],
    shake: [2.4, 200],
    flash: [COLORS.AMBER_WARN, 0.2],
  },
  /** T7 near-loss critical (≤2 lives) — layered over the per-hit beat. */
  mainframeCritical: {
    shake: [4, 260],
    hitStop: "micro",
  },
  /** Wave clear (S189 celebration layer, values unchanged). */
  waveClear: {
    flash: [COLORS.SENTINEL_GREEN, 0.32],
    shake: [2.2, 240],
    vignette: [COLORS.SENTINEL_GREEN, 650],
  },
  /** Tower deploy "thunk" (BL-240, values unchanged). */
  towerPlace: {
    shake: [0.8, 90],
  },
  /** Tower upgrade burst (S43 lineage, values unchanged; flash colour is the
   *  tower's own element colour at request time). */
  towerUpgrade: {
    shake: [2, 120],
  },
} as const satisfies Record<string, FeelBeat>;

export const TOWER_UPGRADE_FLASH_ALPHA = 0.12;

/** Near-loss bands (BL-283): 0 = safe (>3 lives) … 3 = last life. */
export function nearLossBand(lives: number): 0 | 1 | 2 | 3 {
  if (lives <= 0 || lives > 3) return 0;
  return (4 - lives) as 1 | 2 | 3;
}

/** Band-scaled sustained danger tint base alpha (pulse ±0.12 rides on top;
 *  total edge alpha stays ≤ 0.44 — the documented photosensitivity bound). */
export const NEAR_LOSS_TINT_BASE = [0, 0.2, 0.26, 0.32] as const;

/** Heartbeat cadence per band (ms between beats; audio-only escalation). */
export const NEAR_LOSS_HEARTBEAT_MS = [0, 800, 650, 520] as const;

/** Music pressure per band (0..1 into MusicDirector.setPressure). */
export const NEAR_LOSS_PRESSURE = [0, 1 / 3, 2 / 3, 1] as const;
